import { useEffect, useRef, useState } from 'react';

/**
 * Live pod/deployment changes from the API's Steve-backed /watch WebSocket.
 *
 * Replaces polling: the page fetches a normal page, then this patches rows as the cluster
 * changes, so the table reflects reality within ~0.5s instead of up to one cache TTL.
 *
 * Deliberately patches the CURRENT PAGE only. Re-fetching on every event would undo the
 * point of paging (a busy cluster emits events constantly), and inserting brand-new rows
 * into a paged, server-sorted list would put them in the wrong position — so a create that
 * belongs on another page is counted, not injected. `pendingNew` lets the page offer a
 * refresh instead of silently showing a stale list.
 *
 * @param clusterId  cluster to watch (falsy disables the socket)
 * @param resource   'pods' | 'deployments'
 * @param opts.enabled     turn the watch off (e.g. non-Rancher cluster)
 * @param opts.namespace   only apply events from this namespace, matching the page filter
 * @param opts.onPatch     (key, flatItem) => void — update an existing row
 * @param opts.onRemove    (key) => void
 * @param opts.onNew       (key, flatItem) => void — a create not already on this page
 */
export default function useResourceWatch(clusterId, resource, opts = {}) {
  const { enabled = true, namespace, onPatch, onRemove, onNew } = opts;
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  // Handlers live in a ref so a re-render (new closure each time) does not tear down and
  // re-open the socket — that would drop events on every keystroke in a filter box.
  const cb = useRef({ onPatch, onRemove, onNew });
  cb.current = { onPatch, onRemove, onNew };

  useEffect(() => {
    if (!clusterId || !enabled) return undefined;

    const token = localStorage.getItem('token') || '';
    const base = process.env.REACT_APP_API_URL || '';
    const wsBase = base.replace(/^http/, 'ws');
    const url = `${wsBase}/clusters/${clusterId}/watch?token=${encodeURIComponent(token)}&resource=${resource}`;

    let ws;
    let closedByUs = false;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      return undefined;                 // watch is an enhancement; never break the page
    }
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (msg.type === 'watch_start') { setConnected(true); return; }
      if (msg.type === 'watch_error') { setConnected(false); return; }

      const obj = msg.data || {};
      const md = obj.metadata || {};
      if (!md.name) return;
      // Honour the page's namespace filter: a change in another namespace is not ours.
      if (namespace && md.namespace !== namespace) return;

      const key = `${md.namespace}/${md.name}`;
      const flat = resource === 'pods' ? flattenPod(obj) : flattenDeployment(obj);

      if (msg.type === 'remove') cb.current.onRemove?.(key);
      else if (msg.type === 'create') cb.current.onNew?.(key, flat);
      else cb.current.onPatch?.(key, flat);
    };

    ws.onclose = () => { if (!closedByUs) setConnected(false); };
    ws.onerror = () => setConnected(false);

    return () => {
      closedByUs = true;
      setConnected(false);
      wsRef.current = null;
      ws.onmessage = null; ws.onclose = null; ws.onerror = null;
      try { ws.close(); } catch (e) { /* already closing */ }
    };
  }, [clusterId, resource, enabled, namespace]);

  return { connected };
}

// These mirror _flatten_pod/_flatten_deployment on the server, because watch events carry
// the raw k8s object while the table renders the flattened shape. Keep them in sync.
export function flattenPod(o) {
  const md = o.metadata || {};
  const st = o.status || {};
  const spec = o.spec || {};
  const cs = st.containerStatuses || [];
  const ready = cs.filter(c => c.ready).length;
  const total = (spec.containers || []).length;
  // A waiting reason (CrashLoopBackOff/ImagePullBackOff) is more useful than the coarse
  // phase, which stays "Running" while a container is crash-looping.
  let status = st.phase || 'Unknown';
  for (const c of cs) {
    const reason = c.state?.waiting?.reason;
    if (reason) { status = reason; break; }
  }
  return {
    name: md.name || '',
    namespace: md.namespace || '',
    status,
    phase: st.phase || 'Unknown',
    ready: `${ready}/${total}`,
    restarts: cs.reduce((n, c) => n + (c.restartCount || 0), 0),
    node: spec.nodeName || '',
    created_at: md.creationTimestamp || '',
  };
}

export function flattenDeployment(o) {
  const md = o.metadata || {};
  const sp = o.spec || {};
  const st = o.status || {};
  return {
    name: md.name || '',
    namespace: md.namespace || '',
    replicas: sp.replicas || 0,
    ready_replicas: st.readyReplicas || 0,
    updated_replicas: st.updatedReplicas || 0,
    available_replicas: st.availableReplicas || 0,
    created_at: md.creationTimestamp || '',
  };
}
